import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Users, ClipboardText, CookingPot, Database, Swap, UploadSimple, SignOut, CaretLeft, List, BookOpen, Trophy, House, BookOpenText, CalendarDots, GearSix, UsersFour, Baby, TextAa, CurrencyEur, Bell, Star, TrendUp, ChartBar, ChatCircle, NotePencil, WhatsappLogo, UserList, ChartLineUp, UsersThree, CalendarCheck, EnvelopeSimple, ListChecks, ArrowsClockwise, Warning, Columns, Pill, Wallet, Ticket, Files, FloppyDisk, ClockCounterClockwise } from '@phosphor-icons/react';
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { BackofficeBell } from '@/components/admin/BackofficeBell';

const API = process.env.REACT_APP_BACKEND_URL;

const sidebarSections = [
  { title: 'DIA A DIA', items: [
    { path: '/admin/inicio', label: 'Início', icon: House },
    { path: '/admin/calendario', label: 'Calendário', icon: CalendarDots },
    { path: '/admin/pacientes', label: 'Pacientes', icon: Users, badgeKey: 'pacientes_sem_nutricionista' },
    { path: '/admin/lista-espera', label: 'Lista de Espera', icon: UserList, badgeKey: 'lista_espera' },
    { path: '/admin/tarefas', label: 'Tarefas', icon: ListChecks, badgeKey: 'tarefas' },
    { path: '/admin/mensagens', label: 'Mensagens', icon: ChatCircle, badgeKey: 'mensagens' },
  ]},
  { title: 'CLÍNICO', items: [
    { path: '/admin/diarios', label: 'Diários', icon: NotePencil, badgeKey: 'diarios' },
    { path: '/admin/questionarios', label: 'Questionários', icon: BookOpenText, badgeKey: 'pre_consulta' },
    { path: '/admin/formularios', label: 'Formulários', icon: Files },
    { path: '/admin/protocolos', label: 'Protocolos', icon: ClipboardText },
    { path: '/admin/recomendacoes', label: 'Recomendações', icon: Star },
    { path: '/admin/desafios', label: 'Desafios', icon: Trophy, badgeKey: 'notas' },
  ]},
  { title: 'CONTEÚDOS', items: [
    { path: '/admin/receitas', label: 'Receitas', icon: CookingPot, badgeKey: 'receitas' },
    { path: '/admin/alimentos', label: 'Alimentos', icon: Database },
    { path: '/admin/ementa-fixa', label: 'Ementa Fixa', icon: CalendarDots },
    { path: '/admin/suplementacao', label: 'Suplementação', icon: Pill },
    { path: '/admin/materiais', label: 'Materiais', icon: BookOpen },
  ]},
  { title: 'MARKETING', adminOnly: true, items: [
    { path: '/admin/marketing', label: 'Marketing', icon: TrendUp },
  ]},
  { title: 'NEGÓCIO', items: [
    { path: '/admin/metricas', label: 'Métricas', icon: ChartLineUp, adminOnly: true },
    { path: '/admin/financeiro', label: 'Financeiro', icon: CurrencyEur, badgeKey: 'team_payments', adminOnly: true },
    { path: '/admin/equipa', label: 'Equipa', icon: UsersFour, adminOnly: true },
    { path: '/admin/financeiro', to: '/admin/financeiro?separador=meus-pagamentos', label: 'Os meus pagamentos', icon: CurrencyEur, badgeKey: 'team_payments_mine', nonAdminOnly: true },
    { path: '/admin/financeiro', to: '/admin/financeiro?separador=prestacoes-por-configurar', label: 'Prestações por configurar', icon: CurrencyEur, badgeKey: 'installments_to_configure', nonAdminOnly: true },
  ]},
  { title: 'CONFIGURAÇÕES', adminOnly: true, items: [
    { path: '/admin/comunicacoes', label: 'Comunicações', icon: EnvelopeSimple },
    { path: '/admin/configuracoes', label: 'Configurações', icon: GearSix, badgeKey: 'installments_to_configure' },
    { path: '/admin/dados', label: 'Dados', icon: FloppyDisk },
    { path: '/admin/ajuda', label: 'Ajuda', icon: BookOpenText },
  ]},
];

export default function AdminLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [badges, setBadges] = useState({});
  const seenRef = useRef('');

  const isActive = (path, to) => {
    const base = location.pathname === path || location.pathname.startsWith(path + '/');
    if (!base) return false;
    const sep = (to || '').split('separador=')[1];
    if (sep) return location.search.includes(`separador=${sep}`);
    if (path === '/admin/financeiro' && user?.role !== 'admin') return !/separador=(meus-pagamentos|prestacoes-por-configurar)/.test(location.search);
    return true;
  };

  // Fetch badges on mount and when location changes
  const fetchBadges = useCallback(async () => {
    try {
      const [navRes, tpRes, itcRes] = await Promise.all([
        axios.get(`${API}/api/admin/nav-badges`).catch(() => ({ data: {} })),
        axios.get(`${API}/api/team/payments/badge`).catch(() => ({ data: { count: 0 } })),
        axios.get(`${API}/api/installments/to-configure`, { params: { count_only: true } }).catch(() => ({ data: { count: 0 } })),
      ]);
      const tpCount = tpRes.data?.count || 0;
      setBadges({ ...(navRes.data || {}), team_payments: tpCount, team_payments_mine: tpCount, installments_to_configure: itcRes.data?.count || 0 });
    } catch {}
  }, []);
  useEffect(() => { fetchBadges(); }, [fetchBadges]);
  // Allow pages to request an immediate badge refresh (e.g. after auto-marking notes read)
  useEffect(() => {
    const handler = () => fetchBadges();
    window.addEventListener('nav-badges-refresh', handler);
    window.addEventListener('installments-changed', handler);
    return () => { window.removeEventListener('nav-badges-refresh', handler); window.removeEventListener('installments-changed', handler); };
  }, [fetchBadges]);
  // Refresh badges when navigating (clears after visiting a page)
  useEffect(() => { const t = setTimeout(fetchBadges, 500); return () => clearTimeout(t); }, [location.pathname, location.search, fetchBadges]);

  // Mark as seen when navigating to badge pages
  useEffect(() => {
    const markSeen = async (type) => {
      try { await axios.put(`${API}/api/admin/nav-badges/mark-seen`, { type }); fetchBadges(); } catch {}
    };
    const sep = new URLSearchParams(location.search).get('separador');
    const visitKey = `${location.pathname}|${sep || ''}`;
    if (location.pathname === '/admin/mensagens' && (!sep || sep === 'mensagens') && badges.mensagens > 0 && seenRef.current !== visitKey) { seenRef.current = visitKey; markSeen('mensagens'); }
    if (location.pathname !== '/admin/mensagens' || (sep && sep !== 'mensagens')) seenRef.current = '';
    if (location.pathname === '/admin/lista-espera' && badges.lista_espera > 0) markSeen('lista_espera');
    // pre_consulta badge clears per-questionnaire (via "Marcar como revisto"), not on page visit
    // diarios is marked inside AdminDiariosPage itself
  }, [location.pathname, location.search, badges, fetchBadges]);

  const Sidebar = ({ mobile }) => (
    <div className={`flex flex-col h-full ${mobile ? '' : ''}`}>
      <div className="p-5 border-b border-brand-border">
        <h1 className="font-heading font-semibold text-lg text-brand-green tracking-tight" data-testid="admin-logo">nutrianapereira</h1>
        <p className="text-[11px] text-brand-muted mt-0.5">Backoffice Clínico</p>
      </div>
      <nav className="flex-1 overflow-y-auto py-3">
        {sidebarSections.map((section, si) => {
          if (section.adminOnly && user?.role !== 'admin') return null;
          return (
            <div key={si} className={si > 0 ? 'mt-4' : ''}>
              {section.title && <p className="px-5 mb-1 text-[9px] font-semibold uppercase tracking-wider text-brand-green/50">{section.title}</p>}
              {section.items.map(({ path, to, label, icon: Icon, badgeKey, adminOnly, ownerOnly, nonAdminOnly }) => {
                if (adminOnly && user?.role !== 'admin') return null;
                if (nonAdminOnly && user?.role === 'admin') return null;
                if (ownerOnly && (user?.email || '').trim().toLowerCase() !== 'ana@nutrianapereira.pt') return null;
                const count = badgeKey ? (badges[badgeKey] || 0) : 0;
                return (
                  <Link key={label} to={to || path} onClick={() => setSidebarOpen(false)}
                    className={`w-full flex items-center gap-3 px-5 py-2.5 text-sm transition-all no-underline ${isActive(path, to) ? 'bg-brand-green/8 text-brand-green font-medium border-r-2 border-brand-green' : 'text-brand-muted hover:text-brand-text hover:bg-brand-bg'}`}
                    data-testid={`admin-nav-${label.toLowerCase().replace(/ /g, '-')}`}>
                    <Icon size={18} weight={isActive(path, to) ? 'duotone' : 'regular'} />
                    <span className="flex-1 text-left">{label}</span>
                    {count > 0 && (
                      <span className="bg-brand-green text-white text-[9px] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1" data-testid={`badge-${badgeKey}`}>
                        {count > 99 ? '99+' : count}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          );
        })}
      </nav>
      <div className="p-4 border-t border-brand-border">
        {user?.role === 'admin' && (
          <button
            onClick={() => { const next = localStorage.getItem('team_view') !== 'true'; localStorage.setItem('team_view', String(next)); window.location.reload(); }}
            className="w-full flex items-center justify-between gap-2 mb-3 px-3 py-2 rounded-xl border border-brand-border hover:bg-brand-bg transition-colors"
            data-testid="team-view-toggle"
            title="Quando ligado, vês as pacientes de toda a equipa"
          >
            <span className="text-[11px] text-brand-text text-left flex items-center gap-1.5"><UsersThree size={14} className="text-brand-muted" /> Ver toda a equipa</span>
            <span className={`relative inline-flex h-4 w-8 items-center rounded-full transition-colors ${typeof window !== 'undefined' && localStorage.getItem('team_view') === 'true' ? 'bg-brand-green' : 'bg-slate-300'}`}>
              <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${typeof window !== 'undefined' && localStorage.getItem('team_view') === 'true' ? 'translate-x-4' : 'translate-x-0.5'}`} />
            </span>
          </button>
        )}
        <div className="text-xs text-brand-muted mb-2 truncate">{user?.name}</div>
        <Link to="/admin/minha-conta" onClick={() => setSidebarOpen(false)} className="flex items-center gap-2 text-sm text-brand-text hover:text-brand-green transition-colors no-underline mb-2" data-testid="admin-nav-definicoes">
          <GearSix size={16} /> Definições
        </Link>
        <button onClick={() => { logout(); navigate('/login'); }} className="flex items-center gap-2 text-sm text-red-500 hover:text-red-600 transition-colors" data-testid="admin-logout">
          <SignOut size={16} /> Sair
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-brand-bg flex" data-testid="admin-layout">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:w-60 lg:flex-col lg:fixed lg:inset-y-0 bg-white border-r border-brand-border z-40">
        <Sidebar />
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/30" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-white shadow-xl">
            <Sidebar mobile />
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 lg:ml-60 min-w-0 w-full">
        {/* Mobile top bar */}
        <header className="lg:hidden sticky top-0 z-30 backdrop-blur-xl bg-brand-bg/80 border-b border-brand-border/60">
          <div className="px-4 py-3 flex items-center justify-between">
            <button onClick={() => setSidebarOpen(true)} className="p-1.5" data-testid="mobile-menu-btn">
              <List size={22} className="text-brand-green" />
            </button>
            <h1 className="font-heading font-semibold text-brand-green text-sm">nutrianapereira</h1>
            <div className="w-8" />
          </div>
        </header>
        <div className="fixed top-1.5 right-3 lg:top-4 lg:right-6 z-40"><BackofficeBell /></div>

        <main className="p-4 md:p-6 lg:p-8 max-w-5xl min-w-0">
          {children}
        </main>
      </div>
    </div>
  );
}
